/**
 * Auth — web parity of iOS LoginView (#436).
 *
 * Same anatomy as the SetupSlide / NovaIntroSlide pages the user sees
 * right after: full-bleed hero photo up top, fade-to-black across the
 * bottom, giant Orbit wordmark near the middle, inline form + blue
 * CTA + ghost socials + bottom "Create an account" link.
 *
 * Explicitly NOT:
 *   • No `<Tabs>` (iOS doesn't tab either — it toggles the whole page)
 *   • No "Sign in to Orbit" title/subtitle (the wordmark IS the title)
 *   • No frosted-glass card (form floats on the black fade, keynote-style)
 *
 * All auth logic is preserved verbatim from the old page: useAuth for
 * email/password, lovable OAuth for Google/Apple, zod validation, theme
 * reset, `from` redirect, and `/welcome` after fresh signup.
 *
 * SUB-1 Phase 4A adds the Marketing pricing checkout resume path:
 * an unauthenticated visitor who picks a plan from `/marketing` gets
 * bounced here with `?redirect=checkout&plan=…&interval=…` and a
 * matching `orbit_checkout_intent` in sessionStorage. On successful
 * login OR signup we consume the intent, call `create-checkout`, and
 * forward to the returned Stripe URL. Stale intents (>30 min old) are
 * dropped silently so a returning studio isn't hijacked by a stray
 * key. On any checkout failure we route to `/` with a toast so the
 * studio isn't stranded on the auth page.
 */
import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Eye, EyeOff } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { z } from 'zod';
import orbitLogoWhite from '@/assets/orbit-retro-logo-white.png';
import { lovable } from '@/integrations/lovable/index';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { NovaSpinner } from '@/components/nova';
import { useCreateCheckout, type SubscriptionPlan } from '@/services/billing';
import { AuthCollageBackground } from '@/components/auth/AuthCollageBackground';
import { EntryFinaleLayer } from '@/components/auth/EntryFinaleLayer';
import { CreateAccountStage } from '@/components/auth/CreateAccountStage';
import { track } from '@/lib/analytics';
import { cancelSignInFinale, playEntrance, playSignInFinale } from '@/components/auth/entryMotion';
// P0-1 — Orbit Business is held back for the 2026-08-28 launch. See
// PAID_PLANS below for why this file cares.
import { BUSINESS_ON_SALE, IS_EARLY_ACCESS, OAUTH_SIGNIN_SHIPPED, TRIAL_SHIPPED, TRIAL_DAYS } from '@/config/launch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

// SUB-1 Phase 4A — sessionStorage key seeded by the Marketing pricing
// CTA when an unauthenticated visitor picks a plan. We consume it on
// the auth page after a successful login OR signup and forward the
// visitor straight to Stripe. See Marketing.tsx `handleCheckout`.
const CHECKOUT_INTENT_KEY = 'orbit_checkout_intent';
// Silently drop intents older than this — a user who signs in hours
// later probably came back for something unrelated.
const CHECKOUT_INTENT_TTL_MS = 30 * 60 * 1000;

type CheckoutIntent = {
  plan: Exclude<SubscriptionPlan, 'free'>;
  interval: 'monthly' | 'yearly';
  at: number;
};

// P0-1 — the allow-list `readCheckoutIntent` validates against, and
// the only choke point for the whole marketing → auth → Stripe resume
// path (all three call sites below go through it).
//
// `src/components/marketing/MarketingPricing.tsx` is off-limits to
// this pass and still renders a live "Choose Business" CTA that seeds
// `orbit_checkout_intent` with `plan: 'orbit_business'` and sends the
// visitor here with `?redirect=checkout`. Business's Stripe prices are
// `price_REPLACE_ME_*`, so resuming that intent would hand a studio a
// 503 seconds after they finished creating an account — the single
// worst moment in the funnel to fail.
//
// Dropping the plan from this list makes the stale intent fail
// validation, so `readCheckoutIntent()` returns null and every caller
// falls through to its normal post-auth navigation. Nothing is
// charged and nothing errors. The marketing CTA itself still needs
// pulling — see the P0-1 report.
const PAID_PLANS: Exclude<SubscriptionPlan, 'free'>[] = BUSINESS_ON_SALE
  ? ['orbit_starter', 'orbit', 'orbit_business']
  : ['orbit_starter', 'orbit'];

function readCheckoutIntent(): CheckoutIntent | null {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_INTENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CheckoutIntent> | null;
    if (
      !parsed ||
      typeof parsed.at !== 'number' ||
      typeof parsed.plan !== 'string' ||
      typeof parsed.interval !== 'string'
    ) {
      return null;
    }
    if (Date.now() - parsed.at > CHECKOUT_INTENT_TTL_MS) return null;
    if (!PAID_PLANS.includes(parsed.plan as CheckoutIntent['plan'])) return null;
    if (parsed.interval !== 'monthly' && parsed.interval !== 'yearly') return null;
    return {
      plan: parsed.plan as CheckoutIntent['plan'],
      interval: parsed.interval,
      at: parsed.at,
    };
  } catch {
    return null;
  }
}

function clearCheckoutIntent() {
  try {
    sessionStorage.removeItem(CHECKOUT_INTENT_KEY);
  } catch {
    /* private mode / disabled storage — non-fatal */
  }
}

// CSS variables reset so a lingering palette from a previous session
// doesn't tint the auth page. Kept identical to the old page's list.
// SIGNUP-1 — the prototype's scrim: a dark pool behind the wordmark
// and form (so the photos frame them instead of competing), fading
// to the wall at the edges and to solid ink at the bottom.
const ENTRY_SCRIM =
  'radial-gradient(ellipse 46% 60% at 50% 46%, rgba(6,7,12,.82) 0%, rgba(6,7,12,.55) 55%, rgba(6,7,12,.12) 100%),' +
  'linear-gradient(180deg, rgba(6,7,12,.35) 0%, rgba(6,7,12,0) 22%, rgba(6,7,12,.55) 70%, #06070C 100%)';

const themeVarsToReset = [
  '--background', '--foreground', '--card', '--card-foreground',
  '--primary', '--primary-foreground', '--secondary', '--secondary-foreground',
  '--muted', '--muted-foreground', '--accent', '--accent-foreground',
  '--border', '--ring',
];

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

// P0-FIX-1 — password reset request. Same email validator as the
// login form so users can't fire a reset with a garbled address.
const resetEmailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

const signupSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  orgName: z.string().min(2, 'Business name must be at least 2 characters'),
});

type Mode = 'login' | 'signup';

export default function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, signIn, signUp } = useAuth();
  const { toast } = useToast();
  const createCheckout = useCreateCheckout();

  // LAUNCH-2 — `?mode=signup` opens the signup form directly.
  //
  // Every acquisition CTA on the marketing page used to point at
  // /early-access, which WAS a capture form. Repointing them at /auth
  // without this would have landed a first-time visitor on "Welcome
  // back, cosmonaut." and a password field — the launch-day funnel
  // dropping people onto a sign-in screen for an account they don't
  // have yet.
  const [mode, setMode] = useState<Mode>(
    searchParams.get('mode') === 'signup' ? 'signup' : 'login',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // LAUNCH-2 — explicit terms acceptance on signup.
  //
  // The signup form had no mention of the Terms at all — not a link,
  // not a line of fine print — and it creates the account that a
  // 7-day trial then puts a card behind. Stripe's own tick-box is
  // requested in `create-checkout`, but it degrades gracefully when
  // the dashboard URL isn't set (see config/launch.ts), so it cannot
  // be the only place acceptance is collected. This one is ours and
  // always runs. Unchecked by default, and it BLOCKS submit rather
  // than being an implied-by-signing-up notice.
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // P0-FIX-1 — password-reset dialog state.  Sits alongside the
  // login/signup shell so the visitor never has to leave /auth.
  const [isResetOpen, setIsResetOpen] = useState(false);
  // While we're roundtripping to `create-checkout`, freeze the auth
  // shell with a "Setting up your checkout…" hint so the sudden pause
  // doesn't feel like a lag.
  const [isResumingCheckout, setIsResumingCheckout] = useState(false);
  // SIGNUP-1 — the page root + the Sign in button, for the entrance
  // and finale motion (entryMotion.ts). `finaleRef` holds the running
  // finale so the `user` effect below waits for it before navigating.
  const rootRef = useRef<HTMLDivElement>(null);
  const signInButtonRef = useRef<HTMLButtonElement>(null);
  const finaleRef = useRef<Promise<void> | null>(null);
  // ?redirect=checkout guard — we only auto-resume when the URL
  // breadcrumb from Marketing.tsx is present. This way a returning
  // studio who happens to have a stale sessionStorage key from an
  // earlier tab doesn't get whisked to Stripe on their next login.
  const wantsCheckoutResume = searchParams.get('redirect') === 'checkout';

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Signup form state
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [orgName, setOrgName] = useState('');

  // LAUNCH-EA-1 — invite-token verification. A waitlist-invite email
  // links here with ?invite=<token>&email=<email>. A valid token
  // unlocks the signup form for exactly this visitor (pre-filled);
  // everyone else in the early-access window sees the toggle below
  // replaced with a "Join the waitlist" link instead of a working
  // "Create an account" button. See supabase/functions/
  // waitlist-verify-invite and IS_EARLY_ACCESS in config/launch.ts.
  const inviteToken = searchParams.get('invite');
  const [inviteState, setInviteState] = useState<'checking' | 'valid' | 'invalid' | 'none'>(
    inviteToken ? 'checking' : 'none',
  );

  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('waitlist-verify-invite', {
          body: { token: inviteToken },
        });
        if (cancelled) return;
        if (error || !data?.ok || !data?.valid) {
          setInviteState('invalid');
          track('invite_invalid');
          return;
        }
        setInviteState('valid');
        track('invite_opened');
        setMode('signup');
        const emailFromInvite = (data.email as string | undefined) || searchParams.get('email');
        if (emailFromInvite) setSignupEmail(emailFromInvite);
      } catch {
        if (!cancelled) {
          setInviteState('invalid');
          track('invite_invalid');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteToken]);

  // FREEWEEK-1 — Mission Control's funnel: one count per visit to this
  // page, and one when an account is made or someone signs in. The
  // password paths count themselves (`authTracked`); a brand-new account
  // reaching the `user` effect without one came through Apple or Google.
  const authTracked = useRef(false);
  useEffect(() => {
    track('auth_viewed', {
      mode: searchParams.get('mode') === 'signup' ? 'signup' : 'login',
      invite: !!searchParams.get('invite'),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // INVITE-1 — a TEAM invite is not a waitlist invite. AcceptInvite
  // bounces a signed-out invitee here as `?redirect=/invite/<token>`,
  // and during early access this page then showed them "Join the
  // waitlist" instead of a signup form — a studio owner could not add
  // a teammate at all. Recognise that redirect, verify the token
  // through the same get_invite_by_token RPC the accept page uses,
  // and unlock the form for exactly that person. The server-side
  // twin is in waitlist-guard, which lets a fresh account through
  // when a live invite exists for its email (Google/Apple never
  // touch this form).
  const redirectParam = searchParams.get('redirect');
  const teamInviteToken =
    redirectParam?.match(/^\/invite\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i)?.[1] ??
    null;

  useEffect(() => {
    if (!teamInviteToken) return;
    let cancelled = false;
    setInviteState('checking');
    (async () => {
      try {
        // Cast through `any` — types.ts hasn't been regenerated for
        // the SEC-1 RPCs (house pattern, see services/invites.ts).
        const { data, error } = await (supabase as any).rpc('get_invite_by_token', {
          p_token: teamInviteToken,
        });
        if (cancelled) return;
        const invite = data as
          | { email?: string; organizations?: { name?: string | null } | null }
          | null;
        if (error || !invite?.email) {
          setInviteState('invalid');
          return;
        }
        setInviteState('valid');
        setMode('signup');
        setSignupEmail(invite.email);
        // The form insists on a business name; the invitee is joining
        // one, so hand them the studio's. accept_invite moves their
        // profile onto the real org the moment they land on /invite.
        if (invite.organizations?.name) setOrgName(invite.organizations.name);
      } catch {
        if (!cancelled) setInviteState('invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamInviteToken]);

  // INVITE-1 — a verified team invitee always comes back to the
  // accept page, whichever way they authenticated.
  const from = teamInviteToken
    ? `/invite/${teamInviteToken}`
    : location.state?.from?.pathname || '/';

  // Force default theme on auth page — same reset the old page did so
  // a stashed `data-palette` from a previous session doesn't tint the
  // page's inputs.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const savedValues: Record<string, string> = {};

    themeVarsToReset.forEach((prop) => {
      savedValues[prop] = root.style.getPropertyValue(prop);
      root.style.removeProperty(prop);
    });

    const savedPalette = root.getAttribute('data-palette');
    root.removeAttribute('data-palette');

    return () => {
      if (savedPalette) root.setAttribute('data-palette', savedPalette);
      Object.entries(savedValues).forEach(([prop, value]) => {
        if (value) root.style.setProperty(prop, value);
      });
    };
  }, []);

  // If the visitor lands here already signed in (bookmark, back
  // button, etc.) and their URL still carries `redirect=checkout`,
  // try to resume the pending checkout. Otherwise honor the standard
  // `from` redirect.
  //
  // LAUNCH-EA-1 — before any of that, a brand-new account has to
  // clear the waitlist gate. Email/password signup already can't
  // reach the signup form without a valid invite token (see
  // inviteState above), but Google/Apple goes through Lovable's
  // cloud-auth bridge and hands back an authenticated session with no
  // chance to check invite status first — waitlist-guard is the
  // after-the-fact check for that path, and running it here (rather
  // than only from the OAuth handlers) catches any other way a fresh
  // session could land on this page too. A RETURNING user — this
  // account existed before this sign-in — always skips straight
  // through; see waitlist-guard's header for the full mechanism and
  // its one open caveat (the server-side session revoke is unverified
  // against a live sign-in).
  // SIGNUP-1 — photos scale in, wordmark blurs in, form rises.
  useEffect(() => {
    playEntrance(rootRef.current);
  }, [mode]);

  // SIGNUP-6 — `?preview=1` (or `?demo=…`) keeps a signed-in reviewer
  // on this page instead of bouncing them to Today, so the entrance,
  // the sign-in finale and account creation can be looked at from a
  // logged-in browser. Nothing else changes: submitting still works.
  const isPreview = searchParams.has('preview') || searchParams.has('demo');

  useEffect(() => {
    if (!user) return;
    if (isPreview) return;

    const proceedPastGate = () => {
      if (!wantsCheckoutResume) {
        // SIGNUP-1 — if the sign-in finale is playing, Today opens
        // when it ends, not before.
        const go = () => navigate(from, { replace: true });
        if (finaleRef.current) finaleRef.current.then(go);
        else go();
        return;
      }
      const intent = readCheckoutIntent();
      if (!intent) {
        navigate(from, { replace: true });
        return;
      }
      // Fire and forget — resume handler does its own navigation on
      // both success (Stripe redirect) and failure (fallback to `/`).
      void resumeCheckout(intent);
    };

    const createdAtMs = user.created_at ? new Date(user.created_at).getTime() : 0;
    const isFreshAccount = createdAtMs > 0 && Date.now() - createdAtMs < 60_000;
    if (isFreshAccount && !authTracked.current) {
      authTracked.current = true;
      track('account_created', { method: 'oauth' });
    }

    if (!IS_EARLY_ACCESS || !isFreshAccount) {
      proceedPastGate();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('waitlist-guard', { body: {} });
        if (cancelled) return;
        if (error || !data?.ok || !data?.allowed) {
          await supabase.auth.signOut();
          navigate(`/early-access?blocked=1&email=${encodeURIComponent(user.email ?? '')}`, { replace: true });
          return;
        }
        proceedPastGate();
      } catch {
        if (cancelled) return;
        await supabase.auth.signOut();
        navigate(`/early-access?blocked=1&email=${encodeURIComponent(user.email ?? '')}`, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, from, wantsCheckoutResume]);

  /**
   * Consume the stashed `orbit_checkout_intent`, invoke create-checkout,
   * and redirect to the returned Stripe URL. On any failure we route
   * to `/` (Today) with a soft toast so the studio isn't stranded on
   * the auth page.
   *
   * `plan`/`interval` are also present as URL query params for
   * breadcrumb/debug, but sessionStorage is the source of truth — the
   * URL can lag behind if the studio manually edited the querystring.
   */
  async function resumeCheckout(intent: CheckoutIntent) {
    setIsResumingCheckout(true);
    clearCheckoutIntent();
    try {
      const url = await createCheckout.mutateAsync({
        plan: intent.plan,
        interval: intent.interval,
      });
      if (url) {
        window.location.href = url;
        return;
      }
      // Empty URL = demo short-circuit inside useCreateCheckout.
      // Fall through to the safe-landing path.
      navigate('/', { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'That didn\'t land';
      toast({
        title: 'Could not open checkout',
        description: `${message}. You can retry from the Business tab.`,
        variant: 'destructive',
      });
      navigate('/', { replace: true });
    } finally {
      setIsResumingCheckout(false);
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (finaleRef.current) return; // SIGNUP-1 — already on the way in

    // SIGNUP-1 — `/auth?demo=finale` plays the sign-in finale on the
    // button press without signing anyone in, then reloads. For
    // reviewing the motion (and for screenshots) without an account.
    if (searchParams.get('demo') === 'finale') {
      finaleRef.current = playSignInFinale(rootRef.current, signInButtonRef.current);
      finaleRef.current.then(() => window.location.reload());
      return;
    }
    setErrors({});

    const result = loginSchema.safeParse({ email: loginEmail, password: loginPassword });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    // ENTRY-OPTIMISTIC-1 — the finale starts on the press and the
    // sign-in runs underneath it, the same way iOS does it now.
    //
    // It used to wait for the whole Supabase round trip and only then
    // play, which is the pause Rennan saw as the page freezing before
    // the sequence. The finale runs 6.4s and a sign-in answers in well
    // under one, so the answer lands while the button is still
    // charging. No spinner: the motion is the progress, and the guard
    // at the top of this function is what stops a second submit.
    //
    // SIGNUP-1 — photos become colour, colour becomes lines, lines
    // become the sphere, the sphere opens Today.
    const playing = playSignInFinale(rootRef.current, signInButtonRef.current);
    finaleRef.current = playing;

    const { error } = await signIn(loginEmail, loginPassword);

    if (error) {
      // Stand it down and put the screen back; `cancelSignInFinale`
      // also stops `playing` ever settling, so nothing navigates.
      cancelSignInFinale();
      finaleRef.current = null;
      setIsLoading(false);
      return;
    }
    authTracked.current = true;
    track('signed_in', { method: 'password' });

    // SUB-1 Phase 4A — resume the Marketing pricing checkout if the
    // studio was in the middle of one. useEffect(user) handles the
    // OAuth path since we can't `await` it here. This path leaves for
    // Stripe rather than for Today, so the finale stands down.
    if (wantsCheckoutResume) {
      cancelSignInFinale();
      finaleRef.current = null;
      setIsLoading(false);
      const intent = readCheckoutIntent();
      if (intent) {
        void resumeCheckout(intent);
        return;
      }
      navigate(from, { replace: true });
      return;
    }

    // The `user` effect navigates once this resolves (and immediately
    // under prefers-reduced-motion, where it resolves at once).
    playing.then(() => {
      finaleRef.current = null;
      navigate(from, { replace: true });
    });
  };

  // LAUNCH-2 — see the comment on the social buttons below.
  const requireTermsFirst = (run: () => void) => () => {
    if (mode === 'signup' && !acceptedTerms) {
      setErrors({
        acceptedTermsOauth:
          'Please accept the Terms of Service and Privacy Policy first.',
      });
      return;
    }
    setErrors({});
    run();
  };

  const handleSignup = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setErrors({});

    // LAUNCH-2 — checked before the schema so the visitor isn't told
    // about the box only after their password clears.
    if (!acceptedTerms) {
      setErrors({
        acceptedTerms: 'Please accept the Terms of Service and Privacy Policy.',
      });
      return;
    }

    const result = signupSchema.safeParse({
      email: signupEmail,
      password: signupPassword,
      fullName,
      orgName,
    });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);
    const { error } = await signUp(
      signupEmail,
      signupPassword,
      fullName,
      orgName,
      // LAUNCH-2 — recorded, not just checked.
      new Date().toISOString(),
    );
    setIsLoading(false);

    if (error) return;
    authTracked.current = true;
    track('account_created', { method: 'password', invited: !!inviteToken, team: !!teamInviteToken });

    // SUB-1 Phase 4A — if the studio bounced here from the Marketing
    // pricing page (redirect=checkout in the URL), skip the /welcome
    // editorial sequence and route straight into Stripe. They've
    // already picked their plan; making them sit through the intro
    // before paying is a friction they didn't ask for. Onboarding
    // still runs after they return from Stripe via the standard
    // profile-completeness gates.
    if (wantsCheckoutResume) {
      const intent = readCheckoutIntent();
      if (intent) {
        void resumeCheckout(intent);
        return;
      }
    }

    // INVITE-1 — a teammate has no studio to set up; they are joining
    // one. Straight to the accept page, which moves their profile onto
    // the inviting org and skips the plan wall that org already clears.
    if (teamInviteToken) {
      navigate(`/invite/${teamInviteToken}`, { replace: true });
      return;
    }

    // Brand-new signups go straight into setup, which has a real URL
    // if they close mid-flow. Sign-in path honors `from`.
    // SIGNUP-8 — was /welcome (ONB-4's 10-slide brand story). The
    // redesigned wizard opens on its own "Welcome to Orbit" screen, so
    // the two welcomes ran back to back; Google/Apple signups already
    // skipped straight to the wizard. /welcome stays routed for replay.
    navigate('/onboarding', { replace: true });
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth('google', {
        redirect_uri: window.location.origin,
      });
      if (error) {
        toast({
          title: 'Google Sign In Failed',
          description: error.message,
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Google Sign In Failed',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setIsAppleLoading(true);
    try {
      const { error } = await lovable.auth.signInWithOAuth('apple', {
        redirect_uri: window.location.origin,
      });
      if (error) {
        toast({
          title: 'Apple Sign In Failed',
          description: error.message,
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Apple Sign In Failed',
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsAppleLoading(false);
    }
  };

  // SIGNUP-2 — account creation is its own stage (CreateAccountStage):
  // invited from the waitlist, joining a teammate's studio, or an
  // invite that can't be used. Sign-in keeps the photo wall below.
  const showInvalidInvite = inviteState === 'invalid' && mode === 'login';
  if (mode === 'signup' || showInvalidInvite || (isPreview && searchParams.get('variant') === 'invalid')) {
    // DEVMODE-1 — `?preview=1&variant=invited|team|invalid|open` picks
    // the account-creation variant to look at from Admin → Preview.
    const previewVariant = isPreview ? searchParams.get('variant') : null;
    const variant: 'invited' | 'team' | 'invalid' | 'open' =
      previewVariant === 'invited' || previewVariant === 'team' || previewVariant === 'invalid' || previewVariant === 'open'
        ? previewVariant
        : showInvalidInvite
          ? 'invalid'
          : teamInviteToken
            ? 'team'
            : inviteToken
              ? 'invited'
              : 'open';
    return (
      <>
        {isResumingCheckout && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm" role="status" aria-live="polite">
            <div className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-[15px] text-white/85">
              <NovaSpinner size={20} />
              Setting up your checkout…
            </div>
          </div>
        )}
        <CreateAccountStage
          variant={variant}
          studioName={teamInviteToken ? orgName : previewVariant === 'team' ? 'Maya Lind Studio' : undefined}
          fullName={fullName}
          setFullName={setFullName}
          orgName={orgName}
          setOrgName={setOrgName}
          email={signupEmail}
          setEmail={setSignupEmail}
          emailLocked={!!(inviteToken || teamInviteToken) && !!signupEmail}
          password={signupPassword}
          setPassword={setSignupPassword}
          acceptedTerms={acceptedTerms}
          setAcceptedTerms={setAcceptedTerms}
          errors={errors}
          isLoading={isLoading}
          onSubmit={() => void handleSignup()}
          onApple={requireTermsFirst(handleAppleSignIn)}
          onGoogle={requireTermsFirst(handleGoogleSignIn)}
          oauthEnabled={OAUTH_SIGNIN_SHIPPED}
          onBack={() => {
            setErrors({});
            if (showInvalidInvite) setInviteState('none');
            else setMode('login');
          }}
          onRequestInvite={() => navigate('/early-access')}
          invalidMessage={
            teamInviteToken
              ? 'That team invite has expired or was already used. Ask your studio to send a new one.'
              : "That invite link isn't valid anymore — it may already have been used. Join the waitlist again and we'll send a fresh one."
          }
          footer={
            <p data-a="text" className="mt-2 text-[14px] text-white/60">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setErrors({});
                  setInviteState('none');
                  setMode('login');
                }}
                className="font-medium text-white underline-offset-4 hover:underline"
              >
                Sign in
              </button>
            </p>
          }
        />
      </>
    );
  }

  return (
    <div ref={rootRef} className="relative min-h-screen w-full overflow-hidden bg-black text-white">
      <style>{`
        @keyframes entryBlueBreath { 0%,100% { box-shadow: 0 12px 40px rgba(47,107,232,.45); } 50% { box-shadow: 0 16px 60px rgba(47,107,232,.75); } }
        @keyframes entryShimmer { 0% { background-position: 200% 0; } 100% { background-position: -50% 0; } }
        @media (prefers-reduced-motion: reduce) {
          .entry-breath, .entry-shimmer { animation: none !important; }
        }
      `}</style>
      {/* SUB-1 Phase 4A — checkout-resume overlay. Covers the entire
          auth page while we round-trip to create-checkout so the studio
          sees a purposeful "setting up your checkout" beat instead of a
          silent pause between "Sign in" success and the Stripe
          redirect. */}
      {isResumingCheckout && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-5 py-4 text-[15px] text-white/85">
            <NovaSpinner size={20} />
            Setting up your checkout…
          </div>
        </div>
      )}

      {/* ── Animated collage background — the web twin of the iOS
          sign-in: photography drifting up/down behind a colourful
          aurora, fading to black toward the form. ── */}
      <AuthCollageBackground scrim={ENTRY_SCRIM} />
      <EntryFinaleLayer />

      {/* SIGNUP-1 — glass back button to the marketing site. */}
      <button
        type="button"
        data-m="1"
        onClick={() => navigate('/orbit')}
        aria-label="Back"
        className="absolute left-4 top-4 z-20 grid h-11 w-11 place-items-center rounded-full bg-white/10 text-white backdrop-blur-xl transition hover:bg-white/15 md:left-7 md:top-6"
      >
        <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* ── Content column — wordmark, form, and everything below it
          flow in one column instead of the wordmark floating
          separately over a form whose top padding GUESSED how tall
          the wordmark would render. That guess ran short on some
          viewports and the form crept up under it — same fix (and
          the fuller writeup) as EarlyAccessSignup's hero. ────────── */}
      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col items-center px-6 pt-[14vh] pb-10">
        {/* WORDMARK-PLAIN-1 — the wordmark, on its own. The orbit
            ellipse and the satellite that circled it every fourteen
            seconds are gone (Rennan, Oct 22), here and in iOS's
            `AuthWordmark`, so the two still agree. */}
        <div data-logo="1" className="relative grid place-items-center px-12 py-5">
          <img
            src={orbitLogoWhite}
            alt="Orbit"
            className="relative h-[120px] w-auto drop-shadow-[0_6px_28px_rgba(0,0,0,0.65)] md:h-[150px]"
          />
        </div>
        {/* #494 Pass 5 — Editorial first-touch tagline.  Sits
            under the wordmark in Fraunces italic to match the
            welcome sequence + hero pages' voice.  Different copy
            per mode so a returning studio and a new signup get a
            slightly different greeting. */}
        <p
          data-m="1"
          className="font-editorial mt-4 text-white/85 text-center px-6"
          style={{ fontSize: 'clamp(18px, 2.6vw, 26px)', textShadow: '0 2px 8px rgba(0,0,0,0.55)' }}
        >
          Welcome back, cosmonaut.
        </p>

        <div className="mt-8 w-full">
          <LoginForm
              email={loginEmail}
              setEmail={setLoginEmail}
              password={loginPassword}
              setPassword={setLoginPassword}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              isLoading={isLoading}
              errors={errors}
              onSubmit={handleLogin}
              buttonRef={signInButtonRef}
              onForgotPassword={() => {
                setErrors({});
                setIsResetOpen(true);
              }}
            />

          {/* LAUNCH-EA-1 — Google/Apple restored. waitlist-guard now
              gates any brand-new account server-side right after
              sign-in completes (see the `user` effect above), so
              hiding these is no longer the mitigation —
              OAUTH_SIGNIN_SHIPPED just flips them on/off like any
              other feature flag now. */}
          {OAUTH_SIGNIN_SHIPPED && (
            <>
              {/* Divider */}
              <div data-m="1" className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-white/15" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-white/50">
                  or
                </span>
                <div className="h-px flex-1 bg-white/15" />
              </div>

              {/* LAUNCH-2 — these buttons render in SIGNUP mode too, and
                  post-launch (`IS_EARLY_ACCESS` false) the
                  waitlist-guard check short-circuits, which made OAuth
                  the fastest route to a brand-new account that had
                  never seen the Terms. Gating the form's checkbox but
                  not these two is gating nothing. In login mode they
                  are untouched — a returning user accepted at signup.
                  `requireTermsFirst` wraps rather than replaces each
                  handler so neither sign-in path changed shape. */}
              <div data-m="1" className="flex flex-col gap-2.5">
                <GhostButton
                  onClick={requireTermsFirst(handleAppleSignIn)}
                  disabled={isAppleLoading}
                  loading={isAppleLoading}
                >
                  <AppleGlyph />
                  Continue with Apple
                </GhostButton>
                <GhostButton
                  onClick={requireTermsFirst(handleGoogleSignIn)}
                  disabled={isGoogleLoading}
                  loading={isGoogleLoading}
                >
                  <GoogleGlyph />
                  Continue with Google
                </GhostButton>
              </div>
            </>
          )}
        </div>

        {/* P0-FIX-1 — password-reset request dialog. Rendered inside
            the auth shell so the visitor never leaves /auth to ask
            for a link. AuthReset (/auth/reset) handles the actual
            new-password entry once they click the email link. */}
        <ResetPasswordDialog
          open={isResetOpen}
          onOpenChange={setIsResetOpen}
          initialEmail={loginEmail}
        />

        {/* Mode toggle — anchored at the bottom */}
        <div data-m="1" className="mt-auto w-full pt-8 text-center text-[14px] text-white/60">
          {IS_EARLY_ACCESS && inviteState !== 'valid' ? (
            <>
              New here?{' '}
              <Link to="/early-access" className="font-medium text-white underline-offset-4 hover:underline">
                Join the waitlist
              </Link>
            </>
          ) : (
            <>
              New here?{' '}
              <button
                type="button"
                onClick={() => {
                  setMode('signup');
                  setErrors({});
                }}
                className="font-medium text-white underline-offset-4 hover:underline"
              >
                Create an account
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Reset password dialog ──────────────────────────────────────────────────
//
// P0-FIX-1 — Sits inside Auth.tsx so the "Forgot password?" flow
// stays a one-page detour instead of a whole extra route. The
// visitor types their email, we call
// `supabase.auth.resetPasswordForEmail` with a redirect back to
// `/auth/reset`, and Supabase sends the recovery email. On success
// we swap the dialog body to a "Check your email" beat with a soft
// close CTA. Nothing here logs the visitor in or exposes whether
// the email address is on file — we always show the same success
// state, so an attacker can't enumerate accounts with the form.

interface ResetPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEmail?: string;
}

function ResetPasswordDialog({
  open,
  onOpenChange,
  initialEmail,
}: ResetPasswordDialogProps) {
  const { toast } = useToast();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Reseed the email input each time the dialog reopens so the
  // visitor doesn't have to retype what they already had in the
  // sign-in field.
  useEffect(() => {
    if (open) {
      setEmail(initialEmail ?? '');
      setIsSent(false);
      setError(undefined);
    }
  }, [open, initialEmail]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);

    const parsed = resetEmailSchema.safeParse({ email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Please enter a valid email address');
      return;
    }

    setIsSending(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        parsed.data.email,
        {
          redirectTo: `${window.location.origin}/auth/reset`,
        },
      );
      if (resetError) {
        // Never leak whether the address exists — surface a generic
        // "couldn't send" hint that reads the same regardless of the
        // underlying reason.
        toast({
          title: "We couldn't send that just now",
          description: 'Give it another try in a minute — the reset service is temporarily unavailable.',
          variant: 'destructive',
        });
        return;
      }
      setIsSent(true);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {isSent ? (
          <div className="space-y-5">
            <DialogHeader>
              <p className="orbit-eyebrow">Reset requested</p>
              <DialogTitle className="font-editorial text-3xl italic tracking-tight">
                Check your email.
              </DialogTitle>
              <DialogDescription className="text-[14px] leading-relaxed text-muted-foreground">
                We just sent a reset link to <span className="text-foreground">{email}</span>.
                It'll expire in an hour — open it on any device to set a new password.
              </DialogDescription>
            </DialogHeader>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex w-full items-center justify-center rounded-2xl py-3.5 text-[15px] font-semibold text-white transition-colors"
              style={{ backgroundColor: '#275FBE' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1E4C99';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#275FBE';
              }}
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={handleSend} className="space-y-5">
            <DialogHeader>
              <p className="orbit-eyebrow">Forgot your password?</p>
              <DialogTitle className="font-editorial text-3xl italic tracking-tight">
                We'll send you a fresh one.
              </DialogTitle>
              <DialogDescription className="text-[14px] leading-relaxed text-muted-foreground">
                Type the email you signed up with. We'll email a link so you
                can set a new password.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-1.5">
              <label
                htmlFor="reset-email"
                className="text-[13px] font-medium text-foreground"
              >
                Email
              </label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                disabled={isSending}
                className="w-full rounded-xl border border-border bg-background px-4 py-3 text-[15px] text-foreground placeholder:text-muted-foreground focus:border-foreground/40 focus:outline-none disabled:opacity-60"
              />
              {error && (
                <p className="pl-1 text-[12px] text-red-500">{error}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSending}
              className="flex w-full items-center justify-center rounded-2xl py-3.5 text-[15px] font-semibold text-white transition-colors disabled:opacity-70"
              style={{ backgroundColor: '#275FBE' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#1E4C99';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#275FBE';
              }}
            >
              {isSending ? (
                <>
                  <NovaSpinner size={16} className="mr-2" />
                  Sending…
                </>
              ) : (
                'Send reset link'
              )}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Forms ──────────────────────────────────────────────────────────────────

interface LoginFormProps {
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  isLoading: boolean;
  errors: Record<string, string>;
  onSubmit: (e: React.FormEvent) => void;
  onForgotPassword: () => void;
  /** SIGNUP-1 — the Sign in button, so the finale can launch from it. */
  buttonRef?: React.RefObject<HTMLButtonElement>;
}

function LoginForm({
  email,
  setEmail,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  isLoading,
  errors,
  onSubmit,
  onForgotPassword,
  buttonRef,
}: LoginFormProps) {
  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      <Field
        id="login-email"
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="you@example.com"
        disabled={isLoading}
        error={errors.email}
        autoComplete="email"
      />
      <div className="flex flex-col gap-1.5">
        <PasswordField
          id="login-password"
          label="Password"
          value={password}
          onChange={setPassword}
          show={showPassword}
          toggle={() => setShowPassword(!showPassword)}
          disabled={isLoading}
          error={errors.password}
          autoComplete="current-password"
        />
        {/* P0-FIX-1 — "Forgot password?" tucked under the password
            field, subtle enough not to compete with the primary CTA
            but reachable enough that a stuck user can find it. */}
        <div data-m="1" className="flex justify-end pt-0.5">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-[13px] text-[#F2EFEB]/70 underline-offset-4 hover:text-white hover:underline"
          >
            Forgot password?
          </button>
        </div>
      </div>
      <PrimaryButton loading={isLoading} type="submit" buttonRef={buttonRef} breathe>
        Sign in
      </PrimaryButton>
    </form>
  );
}

// SIGNUP-1 — the prototype's glass field: 54pt, 16px radius, no
// border, a 1px inset hairline, translucent white.
const INPUT_CLASS =
  'h-[54px] w-full rounded-2xl border-0 bg-white/[0.08] px-[18px] text-[16px] text-[#F2EFEB] shadow-[inset_0_0_0_1px_rgba(255,255,255,.12)] outline-none placeholder:text-[#F2EFEB]/40 focus:shadow-[inset_0_0_0_1px_rgba(255,255,255,.3)] disabled:opacity-60';

interface FieldProps {
  id: string;
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  autoComplete?: string;
}

function Field({
  id,
  label,
  type,
  value,
  onChange,
  placeholder,
  disabled,
  error,
  autoComplete,
}: FieldProps) {
  return (
    <div data-m="1" className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-[14px] font-medium text-[#F2EFEB]/90"
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        className={INPUT_CLASS}
      />
      {error && (
        <p className="pl-1 text-[12px] text-red-300/90">{error}</p>
      )}
    </div>
  );
}

interface PasswordFieldProps
  extends Omit<FieldProps, 'type'> {
  show: boolean;
  toggle: () => void;
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  show,
  toggle,
  disabled,
  error,
  autoComplete,
}: PasswordFieldProps) {
  return (
    <div data-m="1" className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className="text-[14px] font-medium text-[#F2EFEB]/90"
      >
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••••"
          disabled={disabled}
          autoComplete={autoComplete}
          className={`${INPUT_CLASS} pr-12 tracking-[.1em]`}
        />
        <button
          type="button"
          onClick={toggle}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-[#F2EFEB]/70 hover:text-white"
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <OrbitIcon name="eye.slash" fallback={EyeOff} className="h-4 w-4" /> : <OrbitIcon name="eye" fallback={Eye} className="h-4 w-4" />}
        </button>
      </div>
      {error && (
        <p className="pl-1 text-[12px] text-red-300/90">{error}</p>
      )}
    </div>
  );
}

// ── Buttons ────────────────────────────────────────────────────────────────

interface PrimaryButtonProps {
  loading?: boolean;
  type?: 'button' | 'submit';
  onClick?: () => void;
  children: React.ReactNode;
  /** SIGNUP-1 — the finale launches from this element. */
  buttonRef?: React.RefObject<HTMLButtonElement>;
  /** SIGNUP-1 — the slow blue glow + light sweep from the prototype. */
  breathe?: boolean;
}

function PrimaryButton({
  loading,
  type = 'button',
  onClick,
  children,
  buttonRef,
  breathe,
}: PrimaryButtonProps) {
  return (
    <button
      ref={buttonRef}
      type={type}
      onClick={onClick}
      disabled={loading}
      data-blue="1"
      className={`entry-breath relative mt-2 flex h-14 w-full items-center justify-center overflow-hidden rounded-full text-[17px] font-semibold text-white transition-colors disabled:opacity-70 ${breathe ? '' : 'shadow-[0_12px_40px_rgba(47,107,232,.45)]'}`}
      /* BRAND-1 — Mission Blue. Literal hex (not a token) because this
         button paints identically on every auth stage. SIGNUP-1 lifts
         it to the prototype's #2F6BE8 with the breathing glow. */
      style={{
        backgroundColor: '#2F6BE8',
        animation: breathe ? 'entryBlueBreath 4s ease-in-out infinite' : undefined,
      }}
    >
      {breathe && (
        <span
          aria-hidden
          className="entry-shimmer pointer-events-none absolute inset-0"
          style={{
            background: 'linear-gradient(110deg,transparent 35%,rgba(255,255,255,.4) 50%,transparent 65%)',
            backgroundSize: '250% 100%',
            animation: 'entryShimmer 5.5s ease-in-out infinite',
          }}
        />
      )}
      <span className="relative flex items-center">
        {loading ? (
          <>
            <NovaSpinner size={16} className="mr-2" />
            Working…
          </>
        ) : (
          children
        )}
      </span>
    </button>
  );
}

interface GhostButtonProps {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
}

function GhostButton({ onClick, disabled, loading, children }: GhostButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex h-[54px] w-full items-center justify-center gap-2.5 rounded-full border-0 bg-white/[0.06] text-[15px] font-medium text-[#F2EFEB] shadow-[inset_0_0_0_1px_rgba(255,255,255,.16)] backdrop-blur-md transition-colors hover:bg-white/[0.1] disabled:opacity-60"
    >
      {loading ? <NovaSpinner size={16} /> : children}
    </button>
  );
}

// ── Social glyphs (inline SVG so no icon-font dependency) ──────────────────

function AppleGlyph() {
  return (
    <svg
      className="h-[18px] w-[18px]"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

function GoogleGlyph() {
  return (
    <svg
      className="h-[18px] w-[18px]"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
        fill="#EA4335"
      />
    </svg>
  );
}
